import { SuppliesDiscountMethod } from "@redotech/cockroachdb-codegen/tables/product/supplies-discount-method";
import { SuppliesDiscountType } from "@redotech/cockroachdb-codegen/tables/product/supplies-discount-type";
import { SuppliesDiscountValueKind } from "@redotech/cockroachdb-codegen/tables/product/supplies-discount-value-kind";
import { accountIdFormat } from "@redotech/redo-account-id";
import {
  suppliesDiscountCodeIdFormat,
  suppliesDiscountIdFormat,
} from "@redotech/redo-marketplace-id";
import { SuppliesDiscountStatus } from "@redotech/redo-supplies-common/discount-status";
import * as z from "zod";

export const SuppliesDiscountMethodSchema = z.nativeEnum(
  SuppliesDiscountMethod,
);
export const SuppliesDiscountTypeSchema = z.nativeEnum(SuppliesDiscountType);
export const SuppliesDiscountValueKindSchema = z.nativeEnum(
  SuppliesDiscountValueKind,
);
export const SuppliesDiscountStatusSchema = z.nativeEnum(
  SuppliesDiscountStatus,
);

/**
 * Benefit config, discriminated on type. A new discount type adds a member
 * here and the union forces every consumer to handle it.
 */
export const SuppliesDiscountConfigSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(SuppliesDiscountType.AmountOff),
    valueKind: SuppliesDiscountValueKindSchema,
    /** Basis points when percentage, minor units when fixed_amount. */
    value: z.number().int().positive(),
  }),
]);
export type SuppliesDiscountConfig = z.infer<
  typeof SuppliesDiscountConfigSchema
>;

const SuppliesDiscountCodeSchema_ = z.object({
  id: suppliesDiscountCodeIdFormat.schema,
  code: z.string(),
});

const SuppliesDiscountSchema_ = z.object({
  id: suppliesDiscountIdFormat.schema,
  method: SuppliesDiscountMethodSchema,
  title: z.string(),
  config: SuppliesDiscountConfigSchema,
  minimumPurchaseAmount: z.number().nullable(),
  startsAt: z.date(),
  endsAt: z.date().nullable(),
  oncePerCustomer: z.boolean(),
  firstTimeCustomersOnly: z.boolean(),
  storefrontMessage: z.string().nullable(),
  codes: z.array(SuppliesDiscountCodeSchema_),
  /** Empty means every merchant may use it. */
  merchantAccountIds: z.array(accountIdFormat.schema),
  /** Derived from the window; never stored. */
  status: SuppliesDiscountStatusSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const SuppliesDiscountSchema: z.ZodType<SuppliesDiscount> =
  SuppliesDiscountSchema_;
export interface SuppliesDiscount extends z.infer<
  typeof SuppliesDiscountSchema_
> {}

export type SuppliesDiscountCode = SuppliesDiscount["codes"][number];
